import { Box, Button, Card, IndexTable, InlineStack, Layout, Page, Text, ButtonGroup } from '@shopify/polaris';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

export default function JobsDashboard() {
    const ACTIVE_JOB_STATUSES = new Set(['PENDING', 'SCHEDULED', 'RUNNING', 'RETRYING']);
    const {
        data: jobs = [],
        isLoading,
        isFetching,
        refetch,
        error,
    } = useQuery({
        queryKey: ['jobs', 'all'],
        queryFn: async () => {
            const response = await axios.get(`${import.meta.env.VITE_API_URL}/api/jobs/all`);
            return Array.isArray(response.data) ? response.data : [];
        },
        staleTime: 30_000,
        refetchOnWindowFocus: false,
        // Auto-refresh while any job is in a non-terminal state; stop when all are completed/failed
        refetchInterval: (query) => {
            const data = Array.isArray(query?.state?.data) ? query.state.data : [];
            const hasActiveJobs = data.some((j) => ACTIVE_JOB_STATUSES.has(j.status));
            return hasActiveJobs ? 20000 : false; // poll every 20s while active
        },
        refetchIntervalInBackground: true,
    });

    const rowMarkup = jobs
        .filter((j) => j?.type !== 'sync_program_collection')
        .map(({ id, type, status, lastRunAt }, index) => (
            <IndexTable.Row id={id} key={id} position={index}>
                <IndexTable.Cell>{id}</IndexTable.Cell>
                <IndexTable.Cell>{type}</IndexTable.Cell>
                <IndexTable.Cell>{status === 'SCHEDULED' ? 'IDLE' : status}</IndexTable.Cell>
                <IndexTable.Cell>
                    <InlineStack align="space-between">
                        <Text as="span">{lastRunAt ? new Date(lastRunAt).toLocaleString() : '—'}</Text>
                        <ButtonGroup>
                            <Button
                                size="slim"
                                onClick={async () => {
                                    try {
                                        await axios.post(`${import.meta.env.VITE_API_URL}/api/jobs/run/${id}`);
                                        await refetch();
                                    } catch (e) {
                                        // no-op; could add a toast here
                                    }
                                }}
                                disabled={status === 'RUNNING' || status === 'PENDING'}
                            >
                                Run
                            </Button>
                        </ButtonGroup>
                    </InlineStack>
                </IndexTable.Cell>
            </IndexTable.Row>
        ));

    // Filter for program collection sync jobs and prepare a lightweight log view
    const collectionJobs = (jobs || [])
        .filter((j) => j?.type === 'sync_program_collection')
        .sort((a, b) => {
            const at = new Date(a.processedAt || a.lastRunAt || a.updatedAt || a.createdAt || 0).getTime();
            const bt = new Date(b.processedAt || b.lastRunAt || b.updatedAt || b.createdAt || 0).getTime();
            return bt - at;
        });

    const collectionLogRows = collectionJobs.map((j, index) => {
        const ranAt = j.processedAt || j.lastRunAt || j.updatedAt || j.createdAt;
        return (
            <IndexTable.Row id={j.id} key={j.id} position={index}>
                <IndexTable.Cell>{j.id}</IndexTable.Cell>
                <IndexTable.Cell>{j.status || '—'}</IndexTable.Cell>
                <IndexTable.Cell>{ranAt ? new Date(ranAt).toLocaleString() : '—'}</IndexTable.Cell>
            </IndexTable.Row>
        );
    });

    return (
        <div className="jobs-dashboard">
            <Page fullWidth>
                <Layout>
                    <Layout.Section>
                        <Card>
                            <InlineStack align="space-between">
                                <Text variant="headingLg" as="h2">
                                    Jobs Dashboard
                                </Text>
                                {/* TO DO: Create job form */}
                                {/* <Button variant="primary" onClick={() => navigate('/jobs/create')}>
                                    Create New Job
                                </Button> */}
                            </InlineStack>
                        </Card>
                    </Layout.Section>
                    <Layout.Section>
                        <Card>
                            <InlineStack align="space-between">
                                <Box paddingBlock="400">
                                    <Text variant="headingLg">{isLoading && !isFetching ? 'Loading jobs…' : 'Jobs'}</Text>
                                </Box>
                                <Box paddingBlock="400">
                                    <Button onClick={() => refetch()} loading={isLoading}>
                                        Refresh
                                    </Button>
                                </Box>
                            </InlineStack>
                            {error ? (
                                <Box paddingBlockStart="200">
                                    <Text tone="critical">Failed to load jobs. Please try again.</Text>
                                </Box>
                            ) : null}
                            <IndexTable
                                resourceName={{ singular: 'job', plural: 'jobs' }}
                                itemCount={jobs.length}
                                selectable={false}
                                headings={[{ title: 'Job ID' }, { title: 'Type' }, { title: 'Status' }, { title: 'Last Run' }]}
                            >
                                {rowMarkup}
                            </IndexTable>
                        </Card>
                    </Layout.Section>

                    {/* Program Collection Sync Logs */}
                    <Layout.Section>
                        <Card>
                            <InlineStack align="space-between">
                                <Box paddingBlock="400">
                                    <Text variant="headingLg">Program Collection Sync Logs</Text>
                                </Box>
                                <Box paddingBlock="400">
                                    <Button onClick={() => refetch()} loading={isLoading}>
                                        Refresh
                                    </Button>
                                </Box>
                            </InlineStack>
                            {collectionJobs.length === 0 ? (
                                <Box paddingBlockStart="200">
                                    <Text>No program collection sync jobs yet.</Text>
                                </Box>
                            ) : (
                                <IndexTable
                                    resourceName={{ singular: 'sync job', plural: 'sync jobs' }}
                                    itemCount={collectionJobs.length}
                                    selectable={false}
                                    headings={[{ title: 'Job ID' }, { title: 'Status' }, { title: 'Ran At' }]}
                                >
                                    {collectionLogRows}
                                </IndexTable>
                            )}
                        </Card>
                    </Layout.Section>
                </Layout>
            </Page>
        </div>
    );
}
